import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none [&_svg]:opacity-80 focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground bg-background",
        ghost: "text-foreground bg-foreground/10 shadow-none border-none",
        error:
          "bg-destructive/10 text-destructive border-transparent shadow-none",
        warning: "bg-warning/10 text-warning border-transparent shadow-none",
        success:
          "bg-success/10 text-success border-transparent shadow-none",
        flat: "bg-primary/10 text-primary border-transparent shadow-none",
        info: "bg-info/10 text-info border-transparent shadow-none",
        muted: "bg-muted text-muted-foreground border-transparent shadow-none",
        // A count inside a toolbar button ("Delete 3"), keycap-style.
        count: "bg-background text-muted-foreground font-[inherit]",
      },
      size: {
        default: "px-1.5 py-0.5 text-xs",
        // Tags inside dense rows (phase list, ledger, field chips).
        sm: "px-1.5 py-0 text-[10px]",
      },
    },
    compoundVariants: [
      { variant: "count", size: "default", class: "h-5 rounded-xs px-1 py-0 text-[0.625rem]" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean };

/**
 * @example
 * import { Badge } from "@/components/ui/badge"
 *
 * export function CampaignStatus({ live, unread }: { live: boolean; unread: number }) {
 *   return (
 *     <div className="flex items-center gap-2">
 *       <Badge variant={live ? "success" : "muted"}>{live ? "Live" : "Paused"}</Badge>
 *       {unread > 0 && <Badge variant="count">{unread}</Badge>}
 *     </div>
 *   )
 * }
 */
function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
